import { Scene } from '../scene/Scene';
import { Renderer } from '../rendering/Renderer';
import { Camera } from '../rendering/Camera';
import { Time } from './Time';
import { Input } from './Input';
import { Config } from './Config';
import * as THREE from 'three';
import { LevelManager } from '../game/LevelManager'; 
import { ProjectileSystem } from '../projectiles';
import { WeaponSystem } from '../game/weapons/WeaponSystem';
import { GameUI } from '../ui/GameUI';

/**
 * Main game engine class
 * Responsible for core game loop, scene management, and system coordination.
 */
export class Engine {
  private static instance: Engine;
  private scene: Scene;
  private renderer: Renderer;
  private camera: Camera;
  private time: Time;
  private input: Input;
  private isRunning: boolean = false;
  private levelManager: LevelManager;
  private gameUI: GameUI;
  private projectileSystem: ProjectileSystem;
  private weaponSystem: WeaponSystem;
  
  private constructor() {
    console.log('Engine initializing...');
    
    // Initialize core components
    this.time = new Time();
    this.input = new Input();
    
    // Initialize three.js components
    this.camera = new Camera();
    this.renderer = new Renderer();
    this.scene = new Scene(this.camera);
    
    // Set the input for the camera
    this.camera.setInput(this.input);
    
    // Initialize the culling manager with the renderer
    // This needs to happen after the scene is created
    this.scene.initializeCullingManager(this.renderer.getRenderer());
    
    // Get projectile system from scene
    const sceneProjectileSystem = this.scene.getProjectileSystem();
    if (!sceneProjectileSystem) {
      console.error('Failed to get ProjectileSystem from scene, creating a new one');
      // Fallback: Initialize projectile system
      this.projectileSystem = new ProjectileSystem(
        this.scene.getThreeScene(),
        this.camera.getThreeCamera(),
        this.scene.getObjectManager()
      );
    } else {
      // Use the projectile system from the scene
      this.projectileSystem = sceneProjectileSystem;
      console.log('Using ProjectileSystem from Scene');
    }

    // Pass the projectile system to the object factory
    this.scene.getObjectFactory().setProjectileSystem(this.projectileSystem);
    console.log('Projectile system passed to ObjectFactory');

    // Initialize weapon system
    this.weaponSystem = new WeaponSystem(
      this.camera,
      this.scene,
      this.input
    );

    // Ensure the weapon system has access to the ObjectManager, ObjectFactory, and ProjectileSystem
    this.weaponSystem.setObjectManager(this.scene.getObjectManager());
    this.weaponSystem.setObjectFactory(this.scene.getObjectFactory());
    this.weaponSystem.setProjectileSystem(this.projectileSystem);
    
    // Ensure the projectile system has the spatial manager for collision detection
    const objectManager = this.scene.getObjectManager();
    const spatialManager = objectManager.getSpatialManager();
    if (spatialManager) {
      this.projectileSystem.setSpatialManager(spatialManager);
      console.log('Engine: Explicitly set SpatialManager on ProjectileSystem for collision detection');
    } else {
      console.error('Engine: Failed to get SpatialManager from ObjectManager');
    }
    
    console.log('Weapon system initialized with ProjectileSystem');
    
    // Initialize level manager
    this.levelManager = LevelManager.getInstance();
    this.levelManager.initialize(this.scene.getObjectFactory());
    
    // Initialize game UI
    this.gameUI = new GameUI(this);
    
    // Set up window resize handling
    window.addEventListener('resize', this.onWindowResize.bind(this));
    
    console.log('Engine initialized successfully');
  }
  
  public static getInstance(): Engine {
    if (!Engine.instance) {
      Engine.instance = new Engine();
    }
    return Engine.instance;
  }
  
  /**
   * Start the engine
   */
  public start(): void {
    if (this.isRunning) return;
    
    console.log('Starting engine...');
    this.isRunning = true;
    this.time.start();
    
    // Don't start the first level automatically
    // The game will start only when the player clicks the start button
    
    this.gameLoop();
  }
  
  public stop(): void {
    this.isRunning = false;
    this.time.stop();
    console.log('Engine stopped');
  }
  
  private gameLoop(): void {
    if (!this.isRunning) return;
    
    requestAnimationFrame(this.gameLoop.bind(this));
    
    // Update time
    this.time.update();
    
    // Update game logic here
    this.update(this.time.deltaTime);
    
    // Render frame
    this.render();
  }
  
  /**
   * Main update method, called each frame
   */
  private update(deltaTime: number): void {
    // Update all systems
    this.input.update();
    this.scene.update(deltaTime);
    this.camera.update(deltaTime);
    
    // Get game state
    const gameState = this.levelManager.getGameState();
    
    // Check if game is over or won
    const isGameActive = gameState && !gameState.isGameOverState() && !gameState.isGameWonState();
    
    // Only update systems if game is active
    if (isGameActive) {
      // Update weapon system - projectile system is now updated in scene.update()
      this.weaponSystem.update(deltaTime);
      
      // Update level manager
      this.levelManager.update(deltaTime);
    }
    
    // Update game UI
    this.gameUI.update();
  }
  
  private render(): void {
    this.renderer.render(this.scene.getThreeScene(), this.camera.getThreeCamera());
  }
  
  private onWindowResize(): void {
    this.camera.onResize(window.innerWidth, window.innerHeight);
    this.renderer.onResize(window.innerWidth, window.innerHeight);
  }
  
  public getScene(): Scene {
    return this.scene;
  }
  
  public getRenderer(): Renderer {
    return this.renderer;
  }
  
  public getCamera(): Camera {
    return this.camera;
  }
  
  public getTime(): Time {
    return this.time;
  }
  
  public getInput(): Input {
    return this.input;
  }
  
  public getLevelManager(): LevelManager {
    return this.levelManager;
  }
  
  /**
   * Get the projectile system
   */
  public getProjectileSystem(): ProjectileSystem {
    return this.projectileSystem;
  }
  
  public getWeaponSystem(): WeaponSystem {
    return this.weaponSystem;
  }
  
  /**
   * Reset the game state
   * Used for restarting after game over or win
   */
  public resetGame(): void {
    console.log('Engine: Resetting game state...');
    
    // Reset level manager
    this.levelManager.reset();
    
    // Reset the scene objects
    this.scene.resetToasters();
    
    // Update UI if needed
    this.gameUI.update();
    
    console.log('Engine: Game state reset complete');
  }
}
